import { useMemo, useState } from "react";
import { HeaderBullet } from "@ugg/shared/components/common/HeaderBullet";
import { TierListTablePlaceholder } from "@ugg/shared/components/TierList/common/TierListTablePlaceholder";
import { TierListTableError } from "@ugg/shared/components/TierList/common/TierListTableError";
import { AD_RAIL_MIN_CONTENT_HEIGHT } from "@ugg/shared/components/TierList/common/min-content-height";
import { useChampionProfileContext } from "@ugg/shared/components/Champions/ChampionProfileContext";
import { useQueryString } from "@ugg/shared/hooks/use-query-string";
import { useValidateQueryParams } from "@ugg/shared/query-params/params-helpers";
import { validParams } from "@ugg/shared/query-params/valid-params";
import { asArray, asText } from "@ugg/shared/utils/safe-coerce";
import {
  AugmentManifest,
  AugmentRarity,
  getAramMayhemAugmentIconUrl,
  useAramMayhemAugmentManifest,
  useAramMayhemPerChampionAugmentsByRarity,
} from "@ugg/shared/hooks/aram-mayhem";
import { AramMayhemSkills } from "./AramMayhemSkills";
import { AramMayhemItems } from "./AramMayhemItems";
import { useAramBuild } from "./use-aram-build";
import {
  AUGMENT_RARITY_BULLET_CLASSES,
  AUGMENT_RARITY_CELL_CLASSES,
  RARITY_LABEL,
  RARITY_ORDER,
  resolveRarityParam,
} from "./augment-rarity";

// ─────────────────────────────────────────────────────────────────────
// ARAM Mayhem champion build page — the whole body of the ARAM Mayhem tab.
//
// Replaces the tier-lettered augment table this page used to render. The feed
// behind it (useAramMayhemPerChampionAugmentsByRarity) carries no tier letters
// at all: each rarity is a flat array already ordered best-first by the
// pipeline, so this renders three ranked sections and never sorts.
//
// Two view modes, toggled by the button at the bottom:
//   collapsed (default)  the top COLLAPSED_PER_RARITY of each rarity
//   expanded             every augment in each rarity
//
// Below the augments sit the skills and items modules, on ARAM's data because
// Mayhem publishes no build data of its own. Both are Mayhem-specific
// components (AramMayhemSkills, AramMayhemItems) rather than the shared build
// page's — see the header comment on either for why, and use-aram-build.ts for
// why no stats appear anywhere on this page.
// ─────────────────────────────────────────────────────────────────────

const COLLAPSED_PER_RARITY = 6;

interface AramMayhemAugmentsProps {
  championKey: number;
  patch: string | undefined;
  // Both come from the champion page's own filter bar (rendered by
  // ChampionProfileFilterManager), validated against the page's valid-params
  // by the caller. `rarity` is the raw query value; resolveRarityParam maps it.
  rarity?: unknown;
  search?: unknown;
  // Foot ad, built by the website and injected so this shared module carries no ad vendor code.
  footAdSlot?: React.ReactNode;
}

export const AramMayhemAugments = ({
  championKey,
  patch,
  rarity: rarityParam,
  search: searchParam,
  footAdSlot,
}: AramMayhemAugmentsProps) => {
  const {
    data: augmentData,
    loading: augmentLoading,
    error: augmentError,
  } = useAramMayhemPerChampionAugmentsByRarity({ championKey, patch, ssr: true });
  const { rarities } = augmentData || {};

  // Augment manifest — shared lookup for icons + display names. The sections
  // can render without it, but the result is unreadable ("#1234" labels), so
  // gate on BOTH fetches. Same URL as the SEO block on this page, so the
  // fetcher's URL-keyed cache dedupes them.
  const {
    data: manifestData,
    loading: manifestLoading,
    error: manifestError,
  } = useAramMayhemAugmentManifest({ patch, ssr: true });
  const manifest = manifestData || ({} as AugmentManifest);

  // Collapsed until the reader asks for more. Client state deliberately: the
  // server renders the collapsed view, and the expanded rows are in the markup
  // either way (see the `hidden` class below), so this flips visibility rather
  // than mounting anything — no hydration mismatch, no layout jump.
  const [expanded, setExpanded] = useState(false);

  // Rarity narrows to a single section; search filters ids by manifest name.
  // Both come off the URL, so a filtered view is shareable and survives reload.
  // Because this feed is already grouped by rarity, the rarity filter drops
  // whole sections and needs no id → rarity lookup.
  const rarity = resolveRarityParam(rarityParam);
  const search = asText(searchParam);
  const sections = useMemo(() => {
    const query = search.trim().toLowerCase();
    return RARITY_ORDER.flatMap((key) => {
      if (rarity && key !== rarity) return [];
      // asArray: the feed is pipeline-produced and its types are an assertion
      // about a remote file, so a wrong-typed rarity degrades to an empty
      // section instead of throwing on .map during render.
      const augmentIds = asArray(rarities?.[key]).filter((augmentId) => {
        if (!query) return true;
        const name = manifest[String(augmentId)];
        return typeof name === "string" && name.toLowerCase().includes(query);
      });
      // Drop a rarity whose every augment filtered out, so a search that only
      // hits Prismatic doesn't leave bare "Gold"/"Silver" headings behind.
      if (augmentIds.length === 0) return [];
      return [{ rarity: key, augmentIds }];
    });
  }, [rarities, manifest, rarity, search]);

  // Nothing to expand into when every section already fits — hide the toggle
  // rather than offering a button that does nothing. A narrow search lands
  // here routinely.
  const hasHiddenAugments = sections.some((section) => section.augmentIds.length > COLLAPSED_PER_RARITY);

  // Server-rendered, like the table it replaces. Gated on BOTH fetches so the
  // sections render once with real content rather than popping in one at a
  // time as manifest lookups resolve.
  let body;
  if (augmentLoading || manifestLoading) {
    body = <TierListTablePlaceholder />;
  } else if (augmentError || manifestError) {
    body = <TierListTableError />;
  } else if (sections.length === 0) {
    body = <div className="py-[36px] text-center text-[14px] text-lavender-50">No augments match these filters.</div>;
  } else {
    body = (
      <div className="flex flex-col gap-[18px]">
        {sections.map(({ rarity: sectionRarity, augmentIds }) => (
          <AugmentRaritySection
            key={sectionRarity}
            rarity={sectionRarity}
            augmentIds={augmentIds}
            manifest={manifest}
            patch={patch || ""}
            expanded={expanded}
          />
        ))}
        {hasHiddenAugments && (
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            // text-center is explicit: a global reset left-aligns <button> text.
            className="w-full rounded-[3px] bg-purple-100 py-[10px] text-center text-[14px] font-bold text-white hover:text-accent-blue-400"
          >
            {expanded ? "View Less Augments" : "View All Augments"}
          </button>
        )}
      </div>
    );
  }

  // Same rail-collapse guard the tier lists use: this module is the whole body
  // of the ARAM Mayhem tab, and the champion page uses the identical sticky-rail
  // flex row (RailAdsContainer).
  return (
    <div className={`flex flex-col gap-[12px] ${AD_RAIL_MIN_CONTENT_HEIGHT}`}>
      <div className="rounded-[3px] bg-[#191937] p-[12px] xxs:p-[18px]">{body}</div>
      <AramMayhemBuildModules championId={championKey} patch={patch} />
      {/* The monoapp positions its ads as Electron views, not DOM. */}
      {process.env.UGG_PUBLIC_PLATFORM !== "app" && footAdSlot}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Skills + Items, both on ARAM's data.
//
// Each module stands alone: a champion with skill data but no item data
// renders just the skills block rather than an empty items frame. Renders
// nothing at all while the fetch is in flight, or when ARAM has no data for
// the champion — these are supplementary modules under the augments, not the
// page's reason to exist, so an empty frame is worse than no frame.
// ─────────────────────────────────────────────────────────────────────

function AramMayhemBuildModules({ championId, patch }: { championId: number; patch: string | undefined }) {
  const {
    skillPriority,
    skillPath,
    evolutionPath,
    summonerSpells,
    startingItems,
    coreItems,
    itemOptions1,
    itemOptions2,
    itemOptions3,
  } = useAramBuild({ championId, patch: patch || "", ssr: true });

  if (!skillPriority && !coreItems) return null;

  return (
    <div className="flex flex-col gap-[12px]">
      {skillPriority && (
        <AramMayhemSkills
          championId={championId}
          skillPriority={skillPriority}
          skillPath={skillPath}
          evolutionPath={evolutionPath}
          summonerSpells={summonerSpells}
        />
      )}
      {coreItems && (
        <AramMayhemItems
          startingItems={startingItems}
          coreItems={coreItems}
          items1={itemOptions1}
          items2={itemOptions2}
          items3={itemOptions3}
          patch={patch || ""}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// One rarity's ranked augments: a rarity-colored heading over a grid of
// cells, in feed order (best first).
//
// Every augment is rendered even when collapsed, with the overflow marked
// `hidden`. Slicing the array instead would drop most of this page's augment
// content out of the server-rendered HTML, and the ARAM Mayhem build page is
// an SEO surface — the point of `ssr: true` above. This way a crawler sees the
// full ranking, the reader sees six, and expanding is a class flip rather than
// a re-fetch.
// ─────────────────────────────────────────────────────────────────────

interface AugmentRaritySectionProps {
  rarity: AugmentRarity;
  augmentIds: number[];
  manifest: AugmentManifest;
  patch: string;
  expanded: boolean;
}

function AugmentRaritySection({ rarity, augmentIds, manifest, patch, expanded }: AugmentRaritySectionProps) {
  return (
    <section aria-label={`Top ${RARITY_LABEL[rarity]} augments`}>
      <HeaderBullet className={`mb-[12px] ${AUGMENT_RARITY_BULLET_CLASSES[rarity]}`}>
        {`Top ${RARITY_LABEL[rarity]} Augments`}
      </HeaderBullet>
      <div className="grid grid-cols-2 gap-[8px] sm:grid-cols-3 lg:grid-cols-6">
        {augmentIds.map((augmentId, index) => (
          <AugmentCell
            key={augmentId}
            augmentId={augmentId}
            name={manifest[String(augmentId)]}
            rarity={rarity}
            patch={patch}
            hidden={!expanded && index >= COLLAPSED_PER_RARITY}
          />
        ))}
      </div>
    </section>
  );
}

interface AugmentCellProps {
  augmentId: number;
  // Absent when the id isn't in the manifest — see the fallback below.
  name: string | undefined;
  rarity: AugmentRarity;
  patch: string;
  hidden: boolean;
}

function AugmentCell({ augmentId, name, rarity, patch, hidden }: AugmentCellProps) {
  // `#id` in a dashed tile is the FALLBACK for an augment missing from the
  // manifest: the ranking still tells you it placed, so the cell stays rather
  // than silently dropping a rank.
  return (
    <div
      className={`flex items-center gap-[8px] rounded-[3px] border p-[6px] ${AUGMENT_RARITY_CELL_CLASSES[rarity]} ${
        hidden ? "hidden" : ""
      }`}
    >
      {name ? (
        <img
          src={getAramMayhemAugmentIconUrl(patch, augmentId)}
          alt={name}
          loading="lazy"
          className="h-[32px] w-[32px] shrink-0 rounded-[2px]"
        />
      ) : (
        <div className="flex h-[32px] w-[32px] shrink-0 items-center justify-center rounded-[2px] border border-dashed border-[#1c2030] bg-[#1a1d2e] font-mono text-[10px] text-[#64748b]">
          ?
        </div>
      )}
      <span className="truncate font-['Barlow'] text-[12px] font-semibold text-white" title={name || `#${augmentId}`}>
        {name || `#${augmentId}`}
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────

const AramMayhemBuild = ({ footAdSlot }: { footAdSlot?: React.ReactNode }) => {
  const { championId, page } = useChampionProfileContext();

  // championId is the numeric champion KEY (62 for MonkeyKing, 145 for
  // Kaisa, ...) — the same identifier the pipeline uses as the filename
  // stem for the per-champion augment JSONs, so it passes through as-is.
  //
  // Patch is resolved against the CHAMPION page's valid params, which is why
  // this validates here rather than using useValidatedTierListParams.
  const queryParams = useQueryString();
  const validateQueryParams = useValidateQueryParams(validParams);
  const { patch, rarity, search } = validateQueryParams(page, queryParams, true);

  return <AramMayhemAugments championKey={championId} patch={patch} rarity={rarity} search={search} footAdSlot={footAdSlot} />;
};

export default AramMayhemBuild;
